import type { DensitySnapshot, InstrumentRef } from "@screener/protocol";
import { tickerForClipboard } from "@/density/copy-ticker";
import { venueOf } from "@/lib/labels";
import type { ChangeTickerBody, MetascalpOutcome, MetascalpState } from "./metascalp-link";
import type { GroupRotation } from "./terminal-group-rotation";
import {
  mappingFor,
  metascalpMappingFor,
  type TerminalKind,
  type TerminalSettings,
} from "./terminal-settings";
import type { LinkCommand, LinkOutcome, LinkState } from "./tiger-link";

/**
 * Чистая логика действия «открыть в терминале» (§10 1.36, §89, design.md D1,
 * D3): доступность, сборка команды, круг групп или связок, тексты. Экран и
 * кнопка только показывают то, что решено здесь.
 */

/** Команда TigerTrade без группы: группу берёт круг в момент отправки (§10 1.45). */
export interface TigerCommand {
  terminal: "tigertrade";
  e: string;
  m: string;
  /** Нативное имя инструмента с биржи (`DensitySnapshot.native_symbol`), не приведённая пара. */
  symbol: string;
}

/** Команда MetaScalp без связки: связку берёт круг в момент отправки (§89 п. 6). */
export interface MetascalpCommand {
  terminal: "metascalp";
  exchange: number;
  market: number;
  /** Запись «копировать тикер» §57 (§89 п. 5), не нативное имя. */
  ticker: string;
}

export type TerminalCommand = TigerCommand | MetascalpCommand;

export type TerminalUnavailableReason = "no_mapping" | "no_native_symbol";

export type TerminalAction =
  | { kind: "available"; command: TerminalCommand }
  /** Соответствие и имя есть, набор групп пуст: отправить некуда, действие ведёт в настройки (design D2). Только TigerTrade. */
  | { kind: "needs_groups"; command: TigerCommand }
  | { kind: "unavailable"; reason: TerminalUnavailableReason };

/** Имя терминала для текстов (§89 п. 7 и спека «Терминал выбирается в настройках»). */
export function terminalName(terminal: TerminalKind): string {
  return terminal === "metascalp" ? "MetaScalp" : "TigerTrade";
}

/** Причина недоступности действия в панели при пустом наборе групп. */
export const NEEDS_GROUPS_REASON_TEXT = "Не выбраны группы окон TigerTrade";

/**
 * Доступно ли действие для плотности. Нет соответствия `"<биржа>:<рынок>"` —
 * `no_mapping`. TigerTrade: пустое `native_symbol` (писатель прежней версии)
 * — `no_native_symbol`, пустой набор групп — `needs_groups`; приведённая пара
 * в `symbol` не подставляется никогда. MetaScalp: тикер — запись «копировать
 * тикер» (она есть у любой плотности), групп нет, пустой набор связок не
 * мешает.
 */
export function terminalActionFor(
  snapshot: DensitySnapshot,
  settings: TerminalSettings,
): TerminalAction {
  const { exchange, market } = snapshot.instrument;
  if (settings.terminal === "metascalp") {
    const codes = metascalpMappingFor(settings, exchange, market);
    if (codes === null) {
      return { kind: "unavailable", reason: "no_mapping" };
    }
    return {
      kind: "available",
      command: {
        terminal: "metascalp",
        exchange: codes.exchange,
        market: codes.market,
        ticker: tickerForClipboard(snapshot),
      },
    };
  }
  const mapping = mappingFor(settings, exchange, market);
  if (mapping === null) {
    return { kind: "unavailable", reason: "no_mapping" };
  }
  if (snapshot.native_symbol === "") {
    return { kind: "unavailable", reason: "no_native_symbol" };
  }
  const command: TigerCommand = {
    terminal: "tigertrade",
    e: mapping.e,
    m: mapping.m,
    symbol: snapshot.native_symbol,
  };
  if (settings.groups.length === 0) {
    return { kind: "needs_groups", command };
  }
  return { kind: "available", command };
}

/** Причина недоступности словами — для детальной панели. */
export function unavailableReasonText(
  reason: TerminalUnavailableReason,
  instrument: InstrumentRef,
  terminal: TerminalKind = "tigertrade",
): string {
  return reason === "no_mapping"
    ? `Для ${venueOf(instrument)} не задано соответствие в ${terminalName(terminal)}`
    : "Демон не прислал биржевое имя инструмента";
}

const LINK_STATE_TEXTS: Record<LinkState, string> = {
  off: "TigerTrade выключен",
  connecting: "Подключение…",
  connected: "Подключено",
  down: "Нет связи",
};

export function linkStateText(state: LinkState): string {
  return LINK_STATE_TEXTS[state];
}

/** Состояние MetaScalp в подсказке: то, что известно о порте, либо «выключен». */
export type MetascalpViewState = MetascalpState | { kind: "off" };

/**
 * Состояние MetaScalp для подсказки: порт по последней попытке (design,
 * решение 5). Выключенный терминал называется выключенным, а не «не отвечает».
 * Ручной порт показывается как есть, пока попыток не было.
 */
export function metascalpStateText(state: MetascalpViewState, manualPort: number | null): string {
  switch (state.kind) {
    case "off":
      return "MetaScalp выключен";
    case "found":
      return `MetaScalp: порт ${state.port}`;
    case "not_found":
      return "MetaScalp: порт не найден";
    case "manual_down":
      return `MetaScalp: порт ${state.port} не отвечает`;
    case "idle":
      return manualPort === null
        ? "MetaScalp: порт будет найден при отправке"
        : `MetaScalp: порт ${manualPort}`;
  }
}

export interface TerminalNotice {
  tone: "ok" | "failed";
  text: string;
}

/**
 * Отправка по кругу (§10 1.45, design D3): резервирует следующую группу
 * набора, шлёт команду и называет исход словами. `not_sent` — кадр в сокет
 * не попал, терминал ничего не получил — возвращает группу в круг; `linked`,
 * `sent`, `rejected` и `unconfirmed` (кадр ушёл, связь оборвалась до ответа,
 * окно могло открыться) оставляют круг сдвинутым. Пустой набор — `null`: отправлять
 * некуда, вызывающий ведёт в настройки.
 */
export async function sendToNextGroup(
  send: (command: LinkCommand) => Promise<LinkOutcome>,
  rotation: Pick<GroupRotation, "reserve" | "release">,
  groups: readonly string[],
  command: TigerCommand,
): Promise<TerminalNotice | null> {
  const reservation = rotation.reserve(groups);
  if (reservation === null) {
    return null;
  }
  const { e, m, symbol } = command;
  const outcome = await send({ e, m, symbol, linkGroup: reservation.group });
  if (outcome.kind === "not_sent") {
    rotation.release(reservation);
  }
  return terminalOutcomeNotice(outcome, command.symbol, reservation.group);
}

/** Исход отправки словами: что ушло и в какую группу, либо что проверить. */
export function terminalOutcomeNotice(
  outcome: LinkOutcome,
  symbol: string,
  linkGroup: string,
): TerminalNotice {
  switch (outcome.kind) {
    case "linked":
    case "sent":
      return { tone: "ok", text: `Отправлено в TigerTrade: ${symbol}, группа ${linkGroup}` };
    case "rejected":
      return { tone: "failed", text: `TigerTrade отклонил команду: ${outcome.message}` };
    case "not_sent":
    case "unconfirmed":
      return {
        tone: "failed",
        text: "TigerTrade не отвечает. Запустите терминал и включите Settings → Local Signal Server",
      };
  }
}

/** Клик по выключенному MetaScalp: запроса нет. */
export const METASCALP_DISABLED_NOTICE = "MetaScalp выключен в настройках терминала";

/** Номер связки в записи MetaScalp: строка из трёх цифр, `1` → `"001"`, `42` → `"042"`. */
export function bindingLabel(binding: number): string {
  return String(binding).padStart(3, "0");
}

/**
 * Отправка в MetaScalp по кругу связок (§89 п. 6): резервирует следующую
 * связку набора; пустой набор — запрос без `binding`, круга нет. `not_sent`
 * (запрос не дошёл) возвращает связку; ответ MetaScalp, в том числе отказ,
 * оставляет круг сдвинутым.
 */
export async function sendToMetascalp(
  send: (body: ChangeTickerBody) => Promise<MetascalpOutcome>,
  rotation: Pick<GroupRotation, "reserve" | "release">,
  bindings: readonly number[],
  command: MetascalpCommand,
  enabled = true,
): Promise<TerminalNotice> {
  // Общий выключатель: выключенный терминал запроса не шлёт и круг не двигает.
  if (!enabled) {
    return { tone: "failed", text: METASCALP_DISABLED_NOTICE };
  }
  const reservation = rotation.reserve(bindings.map(bindingLabel));
  const body: ChangeTickerBody = {
    exchange: command.exchange,
    market: command.market,
    ticker: command.ticker,
    ...(reservation === null ? {} : { binding: reservation.group }),
  };
  const outcome = await send(body);
  if (outcome.kind === "not_sent" && reservation !== null) {
    rotation.release(reservation);
  }
  return metascalpOutcomeNotice(outcome, command.ticker, reservation?.group ?? null);
}

/** Исход отправки в MetaScalp словами (§89 п. 7). */
export function metascalpOutcomeNotice(
  outcome: MetascalpOutcome,
  ticker: string,
  binding: string | null,
): TerminalNotice {
  switch (outcome.kind) {
    case "ok":
      return {
        tone: "ok",
        text:
          binding === null
            ? `Отправлено в MetaScalp: ${ticker}`
            : `Отправлено в MetaScalp: ${ticker}, связка ${binding}`,
      };
    case "rejected":
      return { tone: "failed", text: `MetaScalp отклонил команду: ${outcome.message}` };
    case "not_sent":
      return { tone: "failed", text: "MetaScalp не отвечает. Запустите терминал" };
  }
}
